import React, {useState} from 'react'
import styled from 'styled-components'
import {formatDate} from '../../utils/time'
import {DASHBOARD, LOGON_CONTAINER} from '../../constants/const'
import {useUserState} from '../../context/UserContext'
import {useUIDispatch} from '../../context/UIContext'
import {useWorkspaceDispatch} from '../../context/WorkspaceContext'
import Header from '../../components/Header/Header'
import HeaderTitle from '../../components/Header/HeaderTitle'
import HeaderButton from '../../components/Header/HeaderButton'
import {_L, useLocaleState} from '../../context/LocaleContext'
import RequestList from '../../components/kiosk/KioskContent/ServiceSequence/RequestList/RequestList'
import {useViewFetch} from '../../utils/fetch'
import {fetchRoom} from '../../api/room'
import {fetchServiceRequestsForRoom} from '../../api/servicerequest'
import {fetchIsRoomAvailable} from '../../api/workspace'
import Clock from '../../components/Clock/Clock'
import ServiceCatalogItems from '../../containers/ServiceCatalog/ServiceCatalogItems'
import {decodeUrlParameters} from '../../utils/helpers'
import * as Security from '../../api/archibus/SecurityApi'

const fetchRoomData = async ({blId, flId, rmId, emId}) => {
  const [room, requests, isRoomAvailable] = await Promise.all(
    [fetchRoom, fetchServiceRequestsForRoom, fetchIsRoomAvailable].map(fn =>
      fn({blId, flId, rmId, emId})
    )
  )

  return {
    room,
    requests,
    isRoomAvailable
  }
}

const RoomQR = () => {
  const {user} = useUserState()
  const uiDispatch = useUIDispatch()
  const workspaceDispatch = useWorkspaceDispatch()
  const localeState = useLocaleState()
  const [showServiceCatalog, setShowServiceCatalog] = useState(false)

  const {employee, guest: isGuest} = user

  const emId = employee.id

  let queryParams = decodeUrlParameters({
    searchString: window.location.search,
    kioskId: localStorage.getItem('archibus_kiosk_id')
  })

  const {bl_id: blId, fl_id: flId, rm_id: rmId} = queryParams.qrParameters

  const {data: roomData} = useViewFetch({
    api: fetchRoomData,
    parameters: {blId, flId, rmId, emId},
    initialState: {room: {}, requests: [], isRoomAvailable: false},
    onChangeOf: [blId, flId, rmId, emId]
  })

  const {room, requests, isRoomAvailable} = roomData
  const {hotelable, rmTypeDesc} = room

  const onCreateRequest = () => {
    setShowServiceCatalog(true)
  }

  const doBooking = () => onBookRoom({blId, flId, rmId})

  const getSpaceAvailableMessage = () =>
    hotelable === 1 && isRoomAvailable
      ? _L({key: 'This space is available', localeState})
      : ''

  const onBookRoom = () => {
    const currentDate = formatDate(new Date(), 'YYYY-MM-DD')
    workspaceDispatch({
      type: 'setWorkspaceSearch',
      payload: {
        date_start: currentDate,
        date_end: currentDate,
        dayPart: 0,
        recurrence: ''
      }
    })

    uiDispatch({
      type: 'setContent',
      payload: {
        content: 'WORKSPACE_BOOKING',
        parentPage: 'ROOM_QR',
        props: {
          roomCode: {blId, flId, rmId},
          isQrView: true
        }
      }
    })
  }

  const onSignOn = async () => {
    if (!isGuest) {
      await Security.logout()
    }
    uiDispatch({type: 'setContent', payload: {content: LOGON_CONTAINER}})
  }

  const onHome = () => {
    uiDispatch({type: 'setContent', payload: {content: DASHBOARD}})
  }

  const onCancelCatalog = () => setShowServiceCatalog(false)

  const signInLabel = isGuest
    ? _L({key: 'Sign On', localeState})
    : _L({key: 'Sign Off', localeState})

  const headerButton = showServiceCatalog ? (
    <HeaderButton label="Cancel" onClick={onCancelCatalog} />
  ) : (
    <>
      <HeaderButton label={signInLabel} onClick={onSignOn} />{' '}
      {!isGuest && (
        <HeaderButton label={_L({key: 'Home', localeState})} onClick={onHome} />
      )}
    </>
  )

  return (
    <StyledContainer>
      <Header
        leftContent={<Clock />}
        centerContent={<HeaderTitle title="Create Request" />}
        rightContent={headerButton}
      />
      <RoomContainer>
        {showServiceCatalog ? (
          <ServiceCatalogItems
            parentPage="ROOM_QR"
            onCatalogItemChange={() => {}}
          />
        ) : (
          <Container>
            <StyledRoom>
              <TitleText>
                {rmId} - {rmTypeDesc}
              </TitleText>
              <FloorText>
                {_L({key: 'Floor', localeState})} {flId}
              </FloorText>
              <SpaceMessage>{getSpaceAvailableMessage()}</SpaceMessage>
            </StyledRoom>
            <ButtonContainer>
              {isRoomAvailable &&
                hotelable === 1 && (
                  <StyledButton onClick={doBooking}>
                    {_L({key: 'BOOK NOW', localeState})}
                  </StyledButton>
                )}

              <StyledButton onClick={onCreateRequest}>
                {_L({key: 'CREATE A SERVICE REQUEST', localeState})}
              </StyledButton>

              <RecentRequestContainer>
                <div>{_L({key: 'RECENT REQUESTS', localeState})}</div>
              </RecentRequestContainer>
            </ButtonContainer>
            <RequestListContainer>
              <RequestList requests={requests} />
            </RequestListContainer>
          </Container>
        )}
      </RoomContainer>
    </StyledContainer>
  )
}

export default RoomQR

const StyledContainer = styled.div`
  height: 100vh;
  width: 100vw;
  box-sizing: border-box;
`

const RoomContainer = styled.div`
  height: calc(100vh - 80px);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(239, 240, 243);
`
const Container = styled.div`
  width: 600px;
  height: 600px;
  box-sizing: border-box;
  background-color: white;
  border-radius: 8px;
  display: grid;
  grid-template-rows: 170px auto 1fr;
  grid-template-columns: 1fr;
  grid-template-areas: 'room' 'button' 'requests';

  @media (max-width: 630px) {
    width: 100vw;
    height: 100%;
    border-radius: 0;
  }

  @media (max-width: 420px) {
    height: calc(100vh - 110px);
    overflow-y: scroll;
  }
`

const StyledRoom = styled.div`
  padding: 24px;
  height: 100%;
  box-sizing: border-box;
  grid-area: room;
`

const TitleText = styled.div`
  font-size: 22px;
  font-weight: 600;
`

const FloorText = styled.div`
  padding-top: 5px;
  color: #a4a9b7;
`

const SpaceMessage = styled.div`
  font-size: 18px;
  font-weight: 600;
  padding: 20px 0 26px 0;
  box-sizing: border-box;
`
const ButtonContainer = styled.div`
  height: 100%;
  grid-area: button;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledButton = styled.button`
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
  background-color: #3395fe;
  border: 2px solid #3395fe;
  color: #ffffff;
  height: 46px;
  border-radius: 6px;
  outline: none;
  width: 90%;
  margin: 4px 0;

  @media (max-width: 420px) {
    font-size: 14px;
  }
`

const RecentRequestContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  height: 46px;
  background: #f3f5fb;
  color: #a4a9b7;
  font-weight: 600;
  margin-top: 10px;
  box-sizing: border-box;
  padding-left: 20px;
`

const RequestListContainer = styled.div`
  height: 100%;
  min-height: 200px;
  grid-area: requests;
  box-sizing: border-box;
  padding: 8px 10px 6px 16px;
`
